import React, { useEffect, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronsUpDown, MapPin, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

/** Free-form place input — searches via OSM Nominatim through the backend. */
export default function PlaceInput({ value, onChange, testId = "place-input" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/geocode`, { params: { q: query } });
        setResults(data.results || []);
      } finally {
        setLoading(false);
      }
    }, 400);
  }, [query]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testId}
          className="w-full justify-between bg-slate-900/50 border-white/10 text-slate-200 hover:bg-slate-900/70 hover:text-amber-300"
        >
          <span className="flex items-center gap-2 truncate">
            <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
            {value ? `${value.name}, ${value.country}` : "Search any city..."}
          </span>
          <ChevronsUpDown className="w-4 h-4 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px] p-0 bg-slate-900 border-white/10" align="start">
        <div className="p-3 border-b border-white/5">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type any city or place..."
              data-testid={`${testId}-search`}
              className="pl-9 bg-slate-950/50 border-white/10 text-slate-200"
            />
            {loading && <Loader2 className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-amber-400 animate-spin" />}
          </div>
        </div>
        <div className="max-h-72 overflow-auto">
          {results.length === 0 && query.length >= 2 && !loading && (
            <div className="p-4 text-sm text-slate-500 text-center">No results</div>
          )}
          {results.length === 0 && query.length < 2 && (
            <div className="p-4 text-sm text-slate-500 text-center">Start typing to search worldwide</div>
          )}
          {results.map((r, i) => (
            <button
              key={i}
              onClick={() => { onChange(r); setOpen(false); setQuery(""); }}
              data-testid={`${testId}-result-${i}`}
              className="w-full text-left px-4 py-3 hover:bg-white/5 border-b border-white/5 transition"
            >
              <div className="text-slate-100 text-sm font-medium">{r.name}{r.country ? `, ${r.country}` : ""}</div>
              <div className="text-xs text-slate-500 truncate">{r.display}</div>
              <div className="text-xs text-amber-400/70 mt-0.5">{r.tz} · {r.lat.toFixed(2)}, {r.lon.toFixed(2)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
